import React, { useMemo } from 'react';
import { Box, Typography, Alert, Stack } from '@mui/material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Selection,
} from '@syncfusion/ej2-react-grids';

import { ScheduledProcedureCodeEditDialog } from '../../dialogs';
import { useAppSelector } from '../../../../../app/store';
import { useScheduledProcedureCodeActions, useScheduledProcedureCodeEditDialog } from './hooks';
import { ActionsTemplate } from './templates';

// Grid settings
const gridSettings = {
  allowPaging: true,
  pageSettings: { pageSize: 10 },
  selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
};

export interface ScheduledProcedureCodesGridProps {
  /** Optional ID for testing */
  testId?: string;
}

/**
 * ScheduledProcedureCodesGrid - Displays encounter scheduled procedure codes with priority ordering
 * 
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for actions and dialog state
 * - Clean component structure
 */
export const ScheduledProcedureCodesGrid: React.FC<ScheduledProcedureCodesGridProps> = ({ testId }) => {
  // Get scheduled procedure codes from Redux store
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  const scheduledProcedureCodes = encounter?.scheduledProcedureCodes || [];
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Sort by priority for consistent display
  const sortedCodes = useMemo(
    () => [...scheduledProcedureCodes].sort((a, b) => (a.priority || 0) - (b.priority || 0)),
    [scheduledProcedureCodes]
  );

  // Use custom hooks
  const { handleMoveUp, handleMoveDown } = useScheduledProcedureCodeActions();
  const { editDialogOpen, selectedCode, handleRowSelection, handleClose } =
    useScheduledProcedureCodeEditDialog(sortedCodes, isEncounterCancelled);

  // Transform data for grid display
  const gridData = useMemo(
    () =>
      sortedCodes.map((code) => ({
        id: code.id,
        priority: code.priority,
        code: code.code || 'N/A',
        description: code.description || 'N/A',
      })),
    [sortedCodes]
  );

  // Create actions template with row context
  const actionsTemplate = useMemo(
    () => (props: any) => {
      const index = sortedCodes.findIndex((c) => c.id === props.id);
      const isFirst = index === 0;
      const isLast = index === sortedCodes.length - 1;

      return (
        <ActionsTemplate
          codeId={props.id}
          isFirst={isFirst}
          isLast={isLast}
          isDisabled={isEncounterCancelled}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
        />
      );
    },
    [sortedCodes, isEncounterCancelled, handleMoveUp, handleMoveDown]
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Scheduled Procedure Codes ({scheduledProcedureCodes.length})
        </Typography>
        {scheduledProcedureCodes.length > 0 && (
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>
            Use arrows to reorder priority • Click on a row to edit code
          </Typography>
        )}
      </Stack>

      {scheduledProcedureCodes.length === 0 ? (
        <Alert severity="info">
          No scheduled procedure codes found for this encounter.
        </Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="auto"
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective
                headerText="Actions"
                width="90"
                textAlign="Center"
                template={actionsTemplate}
                allowSorting={false}
              />
              <ColumnDirective
                field="priority"
                headerText="Priority"
                width="80"
                textAlign="Center"
              />
              <ColumnDirective
                field="code"
                headerText="Code"
                width="120"
                textAlign="Center"
              />
              <ColumnDirective
                field="description"
                headerText="Description"
                width="300"
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection]} />
          </GridComponent>
        </Box>
      )}

      {/* Edit Dialog */}
      <ScheduledProcedureCodeEditDialog
        open={editDialogOpen}
        scheduledProcedureCode={selectedCode}
        onClose={handleClose}
        onScheduledProcedureCodeUpdated={handleClose}
      />
    </Box>
  );
};

export default ScheduledProcedureCodesGrid;
